/**
 * Watchdog · Notification-Center — Detailansicht (FR-5)
 *
 * Ziel des Slack-Deep-Links (`notificationDeepLink()` → /watchdog/notifications/:id).
 *
 * Aufbau nach PRD 2.2:
 *   1. Menschenlesbare Begründung — `explanation` aus twd_scoring_definitions
 *      plus die Ist/Soll-Werte aus dem `details`-JSON des Scoring-Results
 *   2. Verweis auf den auslösenden Lauf (`run_ref`)
 *   3. Expertenansicht — der Scanbericht tabellarisch, der fehlerauslösende
 *      Wert rot hervorgehoben
 *   4. Freigabe-Aktionen — „Alarm ist valide" / „Fehlalarm"
 *
 * Der Scanbericht kommt aus BigQuery und wird nur auf Anfrage geladen
 * (`?include=run`). Fällt er aus, bleibt die Seite trotzdem entscheidbar.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableRow, TableCell, Chip, Button, Divider,
  CircularProgress, Alert, Stack, Snackbar, Accordion, AccordionSummary, AccordionDetails,
  Tooltip, IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate, useParams } from 'react-router-dom';
import { colors } from '../../styles/theme.js';
import { api } from '../../api/client.js';
import type {
  NotificationDetailResponse, NotificationListItem, NotificationStatus,
} from '../../../shared/watchdogTypes.js';
import { SEVERITY_META, STATUS_META, formatDate } from './NotificationCenterPage.js';

/** Die Schlüssel, unter denen ein Scoring-Result den Soll- und den Ist-Wert ablegt. */
const EXPECTED_KEYS = ['expected', 'soll', 'expected_value', 'baseline'];
const ACTUAL_KEYS = ['actual', 'ist', 'actual_value', 'current'];
const FIELD_KEYS = ['field', 'feld', 'path', 'key'];

function pick(details: Record<string, unknown>, keys: string[]): unknown {
  for (const k of keys) if (details[k] !== undefined) return details[k];
  return undefined;
}

/** Werte lesbar machen, ohne sie zu verfälschen: Objekte als JSON, alles andere als Text. */
function render(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

/** Eine Zeile der Expertenansicht. `highlight` markiert den fehlerauslösenden Wert. */
function KvRow({ label, value, highlight = false }: { label: string; value: unknown; highlight?: boolean }) {
  return (
    <TableRow>
      <TableCell sx={{ width: 260, fontWeight: 600, verticalAlign: 'top', color: colors.black60 }}>
        {label}
      </TableCell>
      <TableCell
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          ...(highlight
            ? { color: colors.scoreRed, fontWeight: 700, bgcolor: 'rgba(239, 68, 68, 0.08)' }
            : {}),
        }}
      >
        {render(value)}
      </TableCell>
    </TableRow>
  );
}

export default function NotificationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<NotificationDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    // include=run holt den Scanbericht für die Expertenansicht mit.
    const res = await api.get<NotificationDetailResponse>(`/watchdog/notifications/${id}?include=run`);
    if (res.success) { setData(res.data); setError(null); } else { setError(res.error.message); }
    setLoading(false);
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  const decide = async (status: NotificationStatus) => {
    if (!id) return;
    setSaving(true);
    setError(null);
    const res = await api.patch<{ notification: NotificationListItem }>(
      `/watchdog/notifications/${id}`,
      { notification_status: status },
    );
    setSaving(false);
    if (res.success) {
      setData((prev) => (prev ? { ...prev, notification: res.data.notification } : prev));
      setToast(status === 'confirmed' ? 'Als valider Alarm bestätigt.' : 'Als Fehlalarm markiert.');
    } else {
      setError(res.error.message);
    }
  };

  const copy = (text: string) => {
    void navigator.clipboard.writeText(text);
    setToast('In die Zwischenablage kopiert.');
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress /></Box>;
  }

  if (error && !data) {
    return (
      <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/watchdog/notifications')} sx={{ mb: 2 }}>
          Zurück zur Übersicht
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data) return null;

  const n = data.notification;
  const sev = SEVERITY_META[n.severity];
  const st = STATUS_META[n.notification_status];

  // Die Ist/Soll-Werte stehen im Scoring-Result in BigQuery, nicht in der
  // Notification selbst — die Tabelle hält nur, WAS angeschlagen hat.
  const scoring = data.result;
  const details = (scoring?.details && typeof scoring.details === 'object'
    ? scoring.details
    : {}) as Record<string, unknown>;

  const expected = pick(details, EXPECTED_KEYS);
  const actual = pick(details, ACTUAL_KEYS);
  const field = pick(details, FIELD_KEYS);
  // Alles, was nicht schon als Ist/Soll/Feld gezeigt wird.
  const restKeys = Object.keys(details).filter(
    (k) => ![...EXPECTED_KEYS, ...ACTUAL_KEYS, ...FIELD_KEYS].includes(k),
  );

  const run = data.run ?? {};
  const runKeys = Object.keys(run).filter((k) => run[k] !== null && run[k] !== undefined);
  // Der fehlerauslösende Wert im Scanbericht — daran hängt die rote Markierung.
  const failingField = typeof field === 'string' ? field : null;

  const decided = n.notification_status !== 'new';

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/watchdog/notifications')} sx={{ mb: 2 }}>
        Zurück zur Übersicht
      </Button>

      {/* ── Kopf ─────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, borderLeft: `6px solid ${sev.color}`, p: 3, mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap' }} useFlexGap>
          <Chip
            label={sev.label}
            size="small"
            sx={{ bgcolor: sev.color, color: sev.text, fontWeight: 700 }}
          />
          <Chip
            label={st.label}
            size="small"
            variant={n.notification_status === 'new' ? 'filled' : 'outlined'}
            sx={{
              fontWeight: 600,
              ...(n.notification_status === 'new'
                ? { bgcolor: st.color, color: colors.white }
                : { borderColor: st.color, color: st.color }),
            }}
          />
          <Typography variant="caption" color="text.secondary">{formatDate(n.created_at)}</Typography>
        </Stack>

        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          {n.question ?? `Scoring ${n.scoring_code}`}
        </Typography>
        {n.target_context && (
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>{n.target_context}</Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          {n.hostname ?? 'unbekannte Domain'} · <code>{n.scoring_code}</code>
          {n.category && <> · {n.category}</>}
        </Typography>
        {n.resolved_at && (
          <Alert severity="success" sx={{ mt: 1.5 }}>
            Derselbe Check hat für dieses Ziel am {formatDate(n.resolved_at)} wieder bestanden — der Vorfall ist erledigt.
            Reißt er erneut, entsteht eine neue Meldung.
          </Alert>
        )}
      </Paper>

      {/* ── 1. Begruendung ───────────────────────────────── */}
      <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>Warum dieser Alarm?</Typography>

        {n.explanation ? (
          <Typography variant="body1" sx={{ mb: 2 }}>{n.explanation}</Typography>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Zu <code>{n.scoring_code}</code> gibt es keine Definition mehr — die Begründung fehlt deshalb.
          </Alert>
        )}

        {(expected !== undefined || actual !== undefined) ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Box sx={{ flex: 1, p: 2, border: `1px solid ${colors.silver}`, borderRadius: 1, bgcolor: colors.lightGrey }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: colors.black60, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Erwartet
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5, whiteSpace: 'pre-wrap' }}>
                {render(expected)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, p: 2, border: `1px solid ${colors.scoreRed}`, borderRadius: 1, bgcolor: 'rgba(239, 68, 68, 0.06)' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: colors.scoreRed, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Vorgefunden
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5, whiteSpace: 'pre-wrap', color: colors.scoreRed, fontWeight: 700 }}>
                {render(actual)}
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Alert severity="info" variant="outlined">
            {data.run_error
              ? 'Die Ist/Soll-Werte konnten nicht geladen werden — BigQuery war nicht erreichbar.'
              : `Zu Lauf ${n.run_ref} liegt kein Scoring-Ergebnis (mehr) vor. `
                + 'Die Begründung oben gilt trotzdem — sie stammt aus der Scoring-Definition.'}
          </Alert>
        )}

        {field !== undefined && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Betroffenes Feld: <code>{render(field)}</code>
          </Typography>
        )}

        {restKeys.length > 0 && (
          <Accordion elevation={0} disableGutters sx={{ mt: 2, '&:before': { display: 'none' }, border: `1px solid ${colors.silver}` }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Weitere Angaben zum Befund ({restKeys.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Table size="small">
                <TableBody>
                  {restKeys.map((k) => <KvRow key={k} label={k} value={details[k]} />)}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        )}
      </Paper>

      {/* ── 2. Ausloesender Lauf ─────────────────────────── */}
      <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>Auslösender Lauf</Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{n.run_ref}</Typography>
          <Tooltip title="run_ref kopieren">
            <IconButton size="small" onClick={() => copy(n.run_ref)}>
              <ContentCopyIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon />}
            onClick={() => navigate(`/watchdog/notifications?run=${encodeURIComponent(n.run_ref)}&status=all`)}
          >
            Alle Befunde dieses Laufs
          </Button>
        </Stack>
        {/* Event- und Ziel-Bezug hängen am Scoring-Result, nicht an der Notification. */}
        {(scoring?.event_ref || scoring?.target_ref) && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {scoring.event_ref && <>Event: <code>{scoring.event_ref}</code> </>}
            {scoring.target_ref && <>Ziel: <code>{scoring.target_ref}</code></>}
          </Typography>
        )}
      </Paper>

      {/* ── 3. Expertenansicht ───────────────────────────── */}
      <Accordion
        elevation={0}
        disableGutters
        sx={{ mb: 3, border: `1px solid ${colors.silver}`, '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#f8fafc' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Expertenansicht — Scanbericht</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {data.run_error ? (
            <Alert severity="warning" sx={{ m: 2 }}>
              Der Scanbericht konnte nicht geladen werden: {data.run_error}
              <br />
              Die Entscheidung lässt sich trotzdem treffen — die Begründung oben steht unabhängig davon.
            </Alert>
          ) : runKeys.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Zu <code>{n.run_ref}</code> liegt kein Scanbericht vor.
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableBody>
                {runKeys.map((k) => (
                  <KvRow
                    key={k}
                    label={k}
                    value={run[k]}
                    // Der fehlerauslösende Wert wird rot hervorgehoben (PRD 2.2, Punkt 3).
                    highlight={failingField !== null && k === failingField}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </AccordionDetails>
      </Accordion>

      {/* ── 4. Freigabe ──────────────────────────────────── */}
      <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Freigabe</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Checks mit „DigA-Freigabe notwendig" (Einstellungen, Watchdog) warten hier, bevor ambix den
          Befund zeigt. Checks ohne diesen Schalter sind in ambix bereits sichtbar. Gelöscht wird
          nichts — eine Entscheidung lässt sich jederzeit ändern.
        </Typography>

        {decided && (
          <Alert severity={n.notification_status === 'confirmed' ? 'error' : 'success'} sx={{ mb: 2 }}>
            {n.notification_status === 'confirmed'
              ? 'Als valider Alarm bestätigt'
              : 'Als Fehlalarm eingestuft'}
            {n.decided_by && <> — von {n.decided_by} am {formatDate(n.decided_at)}</>}
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Divider sx={{ mb: 2 }} />

        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Button
            variant={n.notification_status === 'confirmed' ? 'outlined' : 'contained'}
            color="error"
            startIcon={<CheckCircleIcon />}
            disabled={saving || n.notification_status === 'confirmed'}
            onClick={() => void decide('confirmed')}
          >
            Alarm ist valide
          </Button>
          <Button
            variant={n.notification_status === 'false_alarm' ? 'outlined' : 'contained'}
            startIcon={<CancelIcon />}
            disabled={saving || n.notification_status === 'false_alarm'}
            onClick={() => void decide('false_alarm')}
          >
            Fehlalarm
          </Button>
          {saving && <CircularProgress size={22} sx={{ alignSelf: 'center' }} />}
        </Stack>
      </Paper>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
